import React, { useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import MenuContent from './MenuContent';
import MenuButcher from './MenuButcher';
import PageSEO from './PageSEO'; // Import the SEO component

const MenuView = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Force enable scrolling - temporary fix
    document.body.style.overflow = 'visible';
    document.documentElement.style.overflow = 'visible';
    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
    
  }, []);

  // Comprehensive SEO configuration for menu page
  const menuSEO = {
    title: "Menu | Kinder's Meats Deli BBQ & Catering - Authentic BBQ & Butcher Counter",
    description: "Explore Kinder's Meats menu featuring authentic BBQ, premium deli sandwiches, cut-to-order steaks, marinated meats, and butcher counter specialties. Fresh-made sides, salads, and signature BBQ dishes across 7 California locations.",
    keywords: [
      "Kinder's Meats menu",
      "BBQ restaurant menu California",
      "authentic BBQ menu",
      "deli sandwich menu",
      "butcher counter meats",
      "cut-to-order steaks",
      "marinated chicken",
      "BBQ menu East Bay",
      "restaurant menu Concord",
      "restaurant menu Brentwood",
      "restaurant menu Martinez",
      "restaurant menu Pleasant Hill",
      "restaurant menu Walnut Creek",
      "restaurant menu Hercules",
      "signature BBQ dishes",
      "Ball Tip Sandwich",
      "smoked BBQ ribs",
      "deli meats and cheeses",
      "fresh-made sides",
      "BBQ catering menu",
      "family restaurant menu",
      "California BBQ specialties",
      "marinated meats by the pound",
      "backyard BBQ food",
      "tailgate food menu"
    ],
    canonicalUrl: "/menu",
    ogImage: "https://kindersmeats.com/images/menu-hero-og.jpg",
    customMeta: {
      'menu:type': 'Restaurant Menu',
      'menu:cuisine': 'BBQ, American, Deli',
      'menu:price-range': '$$',
      'menu:dietary-options': 'Various options available'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Menu",
          "@id": "https://kindersmeats.com/menu#menu",
          "name": "Kinder's Meats Restaurant Menu",
          "description": "Complete menu featuring authentic BBQ, premium deli selections, and butcher counter specialties",
          "url": "https://kindersmeats.com/menu",
          "inLanguage": "en-US",
          "provider": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "hasMenuSection": [
            {
              "@type": "MenuSection",
              "@id": "https://kindersmeats.com/menu#bbq-section",
              "name": "BBQ Specialties",
              "description": "Authentic slow-smoked BBQ favorites and signature dishes",
              "hasMenuItem": [
                {
                  "@type": "MenuItem",
                  "name": "Ball Tip Sandwich",
                  "description": "Kinder's most famous sandwich - our signature marinated ball tip beef",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  },
                  "menuAddOn": [
                    {
                      "@type": "MenuSection",
                      "name": "Sides",
                      "hasMenuItem": [
                        {
                          "@type": "MenuItem",
                          "name": "BBQ Beans"
                        },
                        {
                          "@type": "MenuItem",
                          "name": "Coleslaw"
                        }
                      ]
                    }
                  ]
                },
                {
                  "@type": "MenuItem",
                  "name": "Slow-Smoked Ribs",
                  "description": "Tender baby back ribs with our signature BBQ sauce",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                },
                {
                  "@type": "MenuItem",
                  "name": "Pulled Pork Sandwich",
                  "description": "Slow-smoked pulled pork with tangy BBQ sauce",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                },
                {
                  "@type": "MenuItem",
                  "name": "Brisket Plate",
                  "description": "House-smoked beef brisket with traditional sides",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                }
              ]
            },
            {
              "@type": "MenuSection",
              "@id": "https://kindersmeats.com/menu#deli-section",
              "name": "Deli Sandwiches & Specialties",
              "description": "Premium deli meats, cheeses, and made-to-order sandwiches",
              "hasMenuItem": [
                {
                  "@type": "MenuItem",
                  "name": "Turkey & Swiss",
                  "description": "Fresh sliced turkey with Swiss cheese on artisan bread",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                },
                {
                  "@type": "MenuItem",
                  "name": "Pastrami Sandwich",
                  "description": "Premium pastrami with mustard and pickles",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                },
                {
                  "@type": "MenuItem",
                  "name": "Italian Combo",
                  "description": "Salami, ham, mortadella with provolone and Italian dressing",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                }
              ]
            },
            {
              "@type": "MenuSection",
              "@id": "https://kindersmeats.com/menu#butcher-section", 
              "name": "From Our Butcher Counter",
              "description": "Cut-to-order steaks, marinated meats, and signature links",
              "hasMenuItem": [
                {
                  "@type": "MenuItem",
                  "name": "Cut-to-Order Steaks",
                  "description": "Premium steaks cut fresh to your specifications",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock",
                    "priceSpecification": {
                      "@type": "UnitPriceSpecification",
                      "price": "Market Price",
                      "unitCode": "LB"
                    }
                  }
                },
                {
                  "@type": "MenuItem",
                  "name": "Marinated Chicken",
                  "description": "Juicy chicken marinated in Kinder's signature blend",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock",
                    "priceSpecification": {
                      "@type": "UnitPriceSpecification",
                      "unitCode": "LB"
                    }
                  }
                },
                {
                  "@type": "MenuItem",
                  "name": "Signature Sausage Links",
                  "description": "House-made sausages with traditional and specialty flavors",
                  "offers": {
                    "@type": "Offer",
                    "priceCurrency": "USD",
                    "availability": "InStock"
                  }
                }
              ]
            },
            {
              "@type": "MenuSection",
              "@id": "https://kindersmeats.com/menu#sides-section",
              "name": "Sides & Salads",
              "description": "Fresh-made sides and salads perfect for any meal",
              "hasMenuItem": [
                {
                  "@type": "MenuItem",
                  "name": "BBQ Beans",
                  "description": "Slow-cooked beans with smoky BBQ flavor"
                },
                {
                  "@type": "MenuItem", 
                  "name": "Coleslaw",
                  "description": "Creamy traditional coleslaw"
                },
                {
                  "@type": "MenuItem",
                  "name": "Potato Salad",
                  "description": "Classic homestyle potato salad"
                },
                {
                  "@type": "MenuItem",
                  "name": "Garden Salad",
                  "description": "Fresh mixed greens with seasonal vegetables"
                }
              ]
            }
          ]
        },
        {
          "@type": "Restaurant",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "hasMenu": {
            "@id": "https://kindersmeats.com/menu#menu"
          },
          "servesCuisine": ["BBQ", "American", "Deli"],
          "priceRange": "$$",
          "acceptsReservations": false,
          "description": "Family-owned BBQ restaurant serving authentic barbecue and premium deli selections since 1946"
        },
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/menu#webpage",
          "url": "https://kindersmeats.com/menu", 
          "name": "Menu | Kinder's Meats Deli BBQ & Catering - Authentic BBQ & Butcher Counter",
          "description": "Explore Kinder's Meats complete menu featuring authentic BBQ, premium deli sandwiches, and butcher counter specialties.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/menu#menu"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/menu#menu"
          },
          "breadcrumb": {
            "@id": "https://kindersmeats.com/menu#breadcrumb"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/menu#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Menu",
              "item": "https://kindersmeats.com/menu"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What are Kinder's signature dishes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our most famous dish is the Ball Tip Sandwich, featuring our signature marinated ball tip beef. We also specialize in slow-smoked ribs, pulled pork, and fresh-cut steaks from our butcher counter."
              }
            },
            {
              "@type": "Question",
              "name": "Do you have the same menu at all locations?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, our core menu featuring BBQ specialties, deli sandwiches, and butcher counter items is available at all 7 California locations, though some seasonal specials may vary."
              }
            },
            {
              "@type": "Question",
              "name": "Can I buy meat by the pound?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely! Our butcher counter offers cut-to-order steaks, marinated meats, and specialty items sold by the pound - perfect for backyard BBQs and home cooking."
              }
            },
            {
              "@type": "Question",
              "name": "Do you offer catering menu options?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we offer comprehensive BBQ catering services with menu options perfect for events, corporate functions, and large gatherings. Visit our catering page for more details."
              }
            }
          ]
        }
      ]
    }
  };
    
  return (
    <>
      {/* SEO Component */}
      <PageSEO {...menuSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column fullHeight is-justify-content-space-between'>
            <div className='is-flex is-flex-direction-column is-justify-content-start'>
              <Navigation 
                colorMode="colorWhite"
              />

              {/* Menu Content */}
              <article itemScope itemType="https://schema.org/Menu">
                <MenuContent 
                    className="pt-5 is-12-mobile is-10-tablet is-10-desktop is-10-widescreen is-10-fullhd is-offset-0-mobile is-offset-1-tablet is-offset-1-desktop is-offset-1-widescreen is-offset-1-fullhd is-align-center"
                    classNameTitle="content-space-160 is-10-mobile is-10-tablet is-8-desktop is-10-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-2-desktop is-offset-1-widescreen is-offset-2-fullhd is-align-center"
                    title="Menu"
                    colorMode="colorBlack"
                />

                {/* Butcher Counter Section */}
                <section itemScope itemType="https://schema.org/MenuSection">
                  <MenuButcher 
                      subtitle="From Our Butcher Counter"
                      body="Our butcher counter is a neighborhood favorite, stocked with tender steaks, Kinder's juicy marinated chicken, and signature links. You'll also find deli meats and cheeses, fresh-made sides and salads perfect for backyard BBQs, tailgate spreads, or easy, flavor-packed meals at home."
                      image="/images/menu-butcher.jpg"
                      imageMobile="/images/menu-butcher-mobile.jpg"
                      imageAlt="Kinder's butcher counter featuring cut-to-order steaks, marinated chicken, and premium deli meats"
                  />
                </section>
              </article>

              {/* Hidden FAQ Section for SEO */}
              <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
                <h2>Menu FAQ</h2>
                
                <div itemScope itemType="https://schema.org/Question">
                  <h3 itemProp="name">What are Kinder's signature dishes?</h3>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <div itemProp="text">
                      Our most famous dish is the Ball Tip Sandwich, featuring our signature marinated ball tip beef. We also specialize in slow-smoked ribs, pulled pork, and fresh-cut steaks from our butcher counter.
                    </div>
                  </div>
                </div>

                <div itemScope itemType="https://schema.org/Question">
                  <h3 itemProp="name">Do you have the same menu at all locations?</h3>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <div itemProp="text">
                      Yes, our core menu featuring BBQ specialties, deli sandwiches, and butcher counter items is available at all 7 California locations, though some seasonal specials may vary.
                    </div>
                  </div>
                </div>

                <div itemScope itemType="https://schema.org/Question">
                  <h3 itemProp="name">Can I buy meat by the pound?</h3>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <div itemProp="text">
                      Absolutely! Our butcher counter offers cut-to-order steaks, marinated meats, and specialty items sold by the pound - perfect for backyard BBQs and home cooking.
                    </div>
                  </div>
                </div>

                <div itemScope itemType="https://schema.org/Question">
                  <h3 itemProp="name">Do you offer catering menu options?</h3>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <div itemProp="text">
                      Yes, we offer comprehensive BBQ catering services with menu options perfect for events, corporate functions, and large gatherings. Visit our catering page for more details.
                    </div>
                  </div>
                </div>
              </section>
            </div>   
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default MenuView;